'use client';

/**
 * TemplateEngine - Main entry point for dynamic template rendering
 * 
 * This component provides the high-level API for rendering JSON templates
 * as React components. It handles:
 * 
 * - Section template rendering
 * - Context setup with sectionData
 * - Error boundaries for graceful failures
 * - Performance optimizations
 */

import React, { useMemo, ErrorInfo, Component } from 'react';
import { TemplateNode, TemplateContext, SectionData, SectionTemplate, BrandColorKey } from '@/types/template-schema';
import { getBrandColor } from '@/config/brand-colors';
import { NodeRenderer } from './NodeRenderer';

// ============================================================================
// Types
// ============================================================================

export interface TemplateEngineProps {
  /** The template to render */
  template: TemplateNode | null | undefined;
  /** Section data for bindings */
  sectionData?: SectionData;
  /** Additional context data */
  additionalContext?: Record<string, unknown>;
  /** Wrapper className */
  className?: string;
  /** Wrapper style */
  style?: React.CSSProperties;
  /** Error fallback */
  fallback?: React.ReactNode;
  /** Enable debug mode */
  debug?: boolean;
}

export interface SectionRendererProps {
  /** The section to render */
  section: SectionTemplate;
  /** Additional context data */
  additionalContext?: Record<string, unknown>;
  /** Wrapper className */
  className?: string;
  /** Wrapper style */
  style?: React.CSSProperties;
  /** Error fallback */
  fallback?: React.ReactNode;
  /** Enable debug mode */
  debug?: boolean;
}

export interface PageRendererProps {
  /** The sections to render */
  sections: SectionTemplate[];
  /** Additional context data */
  additionalContext?: Record<string, unknown>;
  /** Enable debug mode */
  debug?: boolean;
}

// ============================================================================
// Error Boundary
// ============================================================================

interface ErrorBoundaryProps {
  fallback?: React.ReactNode;
  children: React.ReactNode;
  debug?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class TemplateErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('TemplateEngine Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      if (this.props.debug) {
        return (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-red-800 font-bold mb-2">Template Rendering Error</h3>
            <pre className="text-sm text-red-600 overflow-auto">
              {this.state.error?.message}
            </pre>
          </div>
        );
      }

      return (
        <div className="p-4 bg-zinc-100 rounded-lg text-zinc-500">
          Unable to render this section
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Context Creator
// ============================================================================

/**
 * Create a template context from section data
 */
function createTemplateContext(
  sectionData?: SectionData,
  additionalContext?: Record<string, unknown>
): TemplateContext {
  return {
    sectionData: sectionData || {},
    getBrandColor: (key: BrandColorKey) => getBrandColor(key),
    ...additionalContext,
  };
}

// ============================================================================
// TemplateEngine Component
// ============================================================================

/**
 * Main template engine component
 * Renders a JSON template with the provided data context
 */
export function TemplateEngine({
  template,
  sectionData,
  additionalContext,
  className,
  style,
  fallback,
  debug = false,
}: TemplateEngineProps): React.ReactElement | null {
  // Create the rendering context
  const context = useMemo(
    () => createTemplateContext(sectionData, additionalContext),
    [sectionData, additionalContext]
  );

  // Handle missing template
  if (!template) {
    if (debug) {
      return (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-700">No template provided</p>
        </div>
      );
    }
    return null;
  }

  // Render with error boundary
  return (
    <TemplateErrorBoundary fallback={fallback} debug={debug}>
      <div className={className} style={style}>
        <NodeRenderer node={template} context={context} nodeKey="root" />
      </div>
    </TemplateErrorBoundary>
  );
}

// ============================================================================
// DynamicSectionRenderer Component
// ============================================================================

/**
 * Renders a complete section using the template engine
 * This is the main component for rendering database-stored sections
 */
export function DynamicSectionRenderer({
  section,
  additionalContext,
  className,
  style,
  fallback,
  debug = false,
}: SectionRendererProps): React.ReactElement | null {
  // Skip inactive sections
  if (!section.isActive) {
    return null;
  }

  // Get the template - prefer section.template, could also generate from type
  const template = section.template as TemplateNode | undefined;

  // Merge section data
  const sectionData = section.sectionData || {};

  // Combine styles from section.styles with provided style
  // Convert textColor to color CSS property
  const sectionStyles = section.styles as React.CSSProperties | Record<string, unknown> | undefined;
  const combinedStyle: React.CSSProperties = {
    ...style,
  };
  
  // Apply section styles, converting textColor to color
  if (sectionStyles) {
    Object.entries(sectionStyles).forEach(([key, value]) => {
      if (key === 'textColor') {
        combinedStyle.color = value as string;
      } else {
        (combinedStyle as Record<string, unknown>)[key] = value;
      }
    });
  }

  // Handle background from section.background
  if (section.background) {
    if (section.background.type === 'COLOR') {
      combinedStyle.backgroundColor = section.background.value;
      if (section.background.opacity !== undefined && section.background.opacity < 1) {
        // Handle opacity - would need rgba conversion
        combinedStyle.opacity = section.background.opacity;
      }
    } else if (section.background.type === 'IMAGE') {
      combinedStyle.backgroundImage = `url(${section.background.value})`;
      combinedStyle.backgroundSize = 'cover';
      combinedStyle.backgroundPosition = 'center';
    }
  }

  // Generate fallback message for missing template
  const defaultFallback = debug ? (
    <div className="p-8 bg-zinc-100 rounded-lg text-center">
      <p className="text-zinc-500 mb-2">Section: {section.title || section.sectionKey}</p>
      <p className="text-zinc-400 text-sm">Type: {section.type || 'Unknown'}</p>
      <p className="text-zinc-400 text-sm">No template defined</p>
    </div>
  ) : fallback;

  return (
    <TemplateEngine
      template={template || null}
      sectionData={sectionData}
      additionalContext={{
        sectionId: section.id,
        sectionKey: section.sectionKey,
        sectionType: section.type,
        ...additionalContext,
      }}
      className={className}
      style={combinedStyle}
      fallback={defaultFallback}
      debug={debug}
    />
  );
}

// ============================================================================
// DynamicPageRenderer Component
// ============================================================================

/**
 * Renders a complete page with multiple sections
 */
export function DynamicPageRenderer({
  sections,
  additionalContext,
  debug = false,
}: PageRendererProps): React.ReactElement {
  // Sort sections by order
  const sortedSections = useMemo(
    () => [...sections].sort((a, b) => a.order - b.order),
    [sections]
  );

  return (
    <div className="dynamic-page">
      {sortedSections.map((section, index) => (
        <DynamicSectionRenderer
          key={section.id || `section-${index}`}
          section={section}
          additionalContext={{
            pageIndex: index,
            totalSections: sortedSections.length,
            ...additionalContext,
          }}
          debug={debug}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Exports
// ============================================================================

export { createTemplateContext, TemplateErrorBoundary };
export default TemplateEngine;



